import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Container,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Button,
  Divider,
  Paper,
  Rating,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from '@mui/material';
import {
  AccessTime,
  PlayCircleOutline,
  Lock,
  Star,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CheckCircle,
  HeadsetMic,
  Videocam,
  CalendarToday,
  People,
  KeyboardArrowRight,
  EmailOutlined,
  ComputerOutlined,
  AssignmentOutlined,
  CardMembershipOutlined,
  HeadsetMicOutlined,
  Launch,
  ChatBubbleOutline,
  PlayArrow,
  ArrowForward,
} from '@mui/icons-material';
import CarouselBanner from '../components/CarouselBanner.js';
import {
  getTurmas,
  getUserCourses,
  getTrilhaAcompanhamento,
  inscreverUsuarioTurma,
  getStatusMatricula,
  getCourses,
  getCourseModulesAdmin,
} from '../services/api.js';
import { getCurrentUser } from '../utils/auth.js';
import IconeTrilha1 from '../assets/icone_trilha2_1.png';
import IconeTrilha2 from '../assets/icone_trilha2_2.png';
import IconeTrilha3 from '../assets/icone_trilha2_3.png';
import IconeTrilha4 from '../assets/icone_trilha2_4.png';
import IconeInscricao1 from '../assets/icone_trilha1_1.png';
import IconeInscricao2 from '../assets/icone_trilha1_2.png';
import IconeInscricao3 from '../assets/icone_trilha1_3.png';
import IconeInscricao4 from '../assets/icone_trilha1_4.png';
import ImgNoSubscribe from '../assets/img_nosubscribe.png';
import ImgInscricaoRealizada from '../assets/img_inscricao_realizada.png';
import ImgFilaEspera from '../assets/img_fila_espera_home.png';
import ImgVagaConfirmada from '../assets/img_vaga_confirmada.png';
import ImgPrazoEncerrado from '../assets/img_prazo_encerrado_home.png';
import ImgCapaCurso from '../assets/img_capa_curso.png';

// -- Sub-Components for this layout --

/** `duracao` from trilha is nr_carga_horaria in minutes */
const formatModuloDuracao = duracao => {
  if (duracao == null || duracao === '') return '2 aulas';
  if (typeof duracao === 'string') return duracao;
  if (typeof duracao !== 'number' || Number.isNaN(duracao)) return '2 aulas';

  return `${duracao} horas`
};

const mapModuloPathToCards = (moduloPath = []) =>
  (Array.isArray(moduloPath) ? moduloPath : []).map((m, idx) => ({
    numero:
      m.numero !== undefined && m.numero !== null
        ? m.numero
        : m.ordem !== undefined && m.ordem !== null
          ? m.ordem
          : idx,
    titulo: m.titulo,
    descricao: m.descricao,
    duracao: m.duracao,
    status: m.status || (m.concluido ? 'finished' : 'locked'),
    concluido: !!m.concluido,
  }));

const ModuleCard = ({ modulo, onClick, readOnly = false }) => {
  const isPreview = readOnly;
  const isLocked = modulo.status === 'locked' && !isPreview;
  const isFinished = modulo.status === 'finished' && !isPreview;
  const isCurrent = modulo.status === 'available' && !isPreview;

  const borderColor = isCurrent ? '#0052CC' : '#EAEAEA';
  const bgColor = isCurrent ? '#F2F8FF' : '#FFFFFF';

  const chipBgColor = isFinished
    ? '#E3FCEF'
    : isCurrent
      ? '#E6F0FF'
      : '#F4F5F7';
  const chipTextColor = isFinished
    ? '#006644'
    : isCurrent
      ? '#0052CC'
      : '#5E6C84';
  const chipLabel = isFinished
    ? 'CONCLUÍDO'
    : isCurrent
      ? 'EM ANDAMENTO'
      : 'BLOQUEADO';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        cursor: isLocked || (isPreview && !onClick) ? 'default' : 'pointer',
        p: 3,
        '&:hover': {
          transform:
            isLocked || (isPreview && !onClick) ? 'none' : 'translateY(-4px)',
          boxShadow:
            isLocked || (isPreview && !onClick)
              ? 'none'
              : '0 12px 24px rgba(0,0,0,0.06)',
        },
      }}
      onClick={!isLocked ? onClick : undefined}
    >
      {/* Top Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: `1px solid ${isFinished ? '#006644' : isCurrent ? '#0052CC' : '#5E6C84'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
          }}
        >
          {isFinished && (
            <CheckCircle sx={{ color: '#006644', fontSize: 18 }} />
          )}
          {isCurrent && (
            <PlayCircleOutline sx={{ color: '#0052CC', fontSize: 18 }} />
          )}
          {isLocked && <Lock sx={{ color: '#5E6C84', fontSize: 18 }} />}
          {isPreview && (
            <AssignmentOutlined sx={{ color: '#5E6C84', fontSize: 18 }} />
          )}
        </Box>
        <Typography
          variant='subtitle1'
          fontWeight='bold'
          sx={{ color: '#1A2340', flexGrow: 1 }}
        >
          Módulo {modulo.numero}
        </Typography>
        {!isPreview && (
          <Chip
            label={chipLabel}
            size='small'
            sx={{
              bgcolor: chipBgColor,
              color: chipTextColor,
              fontWeight: 'bold',
              fontSize: '0.65rem',
              height: 20,
              borderRadius: 1,
              px: 0.5,
            }}
          />
        )}
      </Box>

      {/* Title & Description */}
      <Box
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', mb: 3 }}
      >
        <Typography
          variant='subtitle1'
          fontWeight='bold'
          sx={{
            mb: 1,
            color: isLocked ? 'text.disabled' : '#1A2340',
            lineHeight: 1.2,
          }}
        >
          {modulo.titulo}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: isLocked ? 'text.disabled' : '#6B778C',
            fontSize: '0.85rem',
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {modulo.descricao ||
            'Familiarize-se com as ferramentas de ensino e aprenda a navegar no gêmeo digital da planta.'}
        </Typography>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          mt: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: '#6B778C',
          }}
        >
          <AccessTime sx={{ fontSize: '1rem' }} />
          <Typography
            variant='body2'
            sx={{ fontSize: '0.8rem', fontWeight: 500 }}
          >
            {formatModuloDuracao(modulo.duracao)}
          </Typography>
        </Box>

        {!readOnly && isFinished && (
          <Typography
            variant='button'
            sx={{
              color: '#8DC63F',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              textTransform: 'none',
            }}
          >
            Revisar módulo &gt;
          </Typography>
        )}

        {!readOnly && isCurrent && (
          <Button
            variant='contained'
            size='small'
            endIcon={<KeyboardArrowRight />}
            sx={{
              bgcolor: '#0A2540',
              color: '#fff',
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              px: 2,
              '&:hover': { bgcolor: '#001b33' },
            }}
          >
            Continuar módulo
          </Button>
        )}
      </Box>
    </Paper>
  );
};

const CourseCard = ({
  curso,
  onContinue,
  onToggleModules,
  modulesExpanded,
  modulos,
  loadingModules,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        bgcolor: '#fff',
        border: '1px solid #EAEAEA',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top Part: Image and Info */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          p: 3,
          gap: 4,
        }}
      >
        {/* Left Side: Image with Overlays */}
        <Box
          sx={{
            width: { xs: '100%', md: 400 },
            height: 240,
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <img
            src={curso.imagem_url || ImgCapaCurso}
            alt={curso.titulo}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {/* Top Left Chip */}
          <Box sx={{ position: 'absolute', top: 16, left: 16 }}>
            <Chip
              label='EM ANDAMENTO'
              sx={{
                bgcolor: 'rgba(0, 102, 68, 0.9)',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                borderRadius: 1,
                height: 24,
              }}
            />
          </Box>
          {/* Bottom Left Icon placeholder */}
        </Box>

        {/* Right Side: Info and Progress */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              label={curso.turma_nome || 'Turma'}
              sx={{
                bgcolor: '#3366FF',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: 1,
                height: 24,
                fontSize: '0.75rem',
              }}
            />
            <Chip
              label={
                curso.modalidade === 'R'
                  ? 'Remota'
                  : curso.modalidade === 'H'
                    ? 'Híbrida'
                    : curso.modalidade || 'Remota'
              }
              sx={{
                bgcolor: curso.modalidade === 'H' ? '#FFF3E0' : '#E6F0FF',
                color: curso.modalidade === 'H' ? '#E65100' : '#3366FF',
                fontWeight: 'bold',
                borderRadius: 1,
                height: 24,
                fontSize: '0.75rem',
              }}
            />
          </Box>

          <Typography
            variant='h5'
            fontWeight='800'
            sx={{ mb: 2, color: '#0A2540', lineHeight: 1.2 }}
          >
            {curso.titulo}
          </Typography>

          <Typography
            variant='body1'
            sx={{
              color: '#6B778C',
              mb: 3,
              lineHeight: 1.5,
              fontSize: '0.95rem',
            }}
          >
            {curso.descricao ||
              'Formação técnica aplicada sobre operação, manutenção, segurança e navegação em ambiente imersivo para reatores Fischer-Tropsch.'}
          </Typography>

          {/* Progress Bar */}
          <Box
            sx={{
              mt: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: '#6B778C', fontWeight: 600 }}
              >
                {curso.modulos_concluidos ?? 0} de{' '}
                {curso.modulos_total || curso.aulas || 6} módulos concluídos
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: '#0A2540', fontWeight: 'bold' }}
              >
                {curso.progress?.porcentagem_concluida || 0}%
              </Typography>
            </Box>
            <LinearProgress
              variant='determinate'
              value={curso.progress?.porcentagem_concluida || 0}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: '#EAEAEA',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#8DC63F',
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#EAEAEA' }} />

      {/* Bottom Part: Next Class and Actions */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {/* Next Class Info / Continue Button Card */}
        <Paper
          elevation={0}
          onClick={onContinue}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2.5,
            py: 1.5,
            borderRadius: '12px',
            bgcolor: '#F4F6F8',
            border: '1px solid #E2E8F0',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: '#F0FDF4',
              borderColor: '#10B981',
              boxShadow: '0 4px 14px rgba(16, 185, 129, 0.15)',
              transform: 'translateY(-1px)',
              '& .continue-icon-box': {
                bgcolor: '#10B981',
                color: '#FFFFFF',
              },
              '& .play-cta-arrow': {
                transform: 'translateX(3px)',
              },
            },
          }}
        >
          <Box
            className='continue-icon-box'
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: '#E3FCEF',
              color: '#006644',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <PlayArrow sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 160 }}>
            <Typography
              variant='caption'
              sx={{
                color: '#10B981',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.7rem',
                display: 'block',
              }}
            >
              {curso?.progress?.porcentagem_concluida === 100 ||
                curso?.concluido
                ? 'Pegue seu Certificado'
                : 'Continuar de onde parou'}
            </Typography>
            {!(
              curso?.progress?.porcentagem_concluida === 100 || curso?.concluido
            ) && (
                <>
                  <Typography
                    variant='body1'
                    fontWeight='bold'
                    sx={{ color: '#0A2540', display: 'block', lineHeight: 1.2 }}
                  >
                    {curso?.currentConteudoTitle || 'Introdução aos Corantes'}
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{ color: '#6B778C', display: 'block', fontWeight: 500 }}
                  >
                    {curso?.currentModuloTitle || 'Módulo 1'}
                  </Typography>
                </>
              )}
          </Box>
          <ArrowForward
            className='play-cta-arrow'
            sx={{
              color: '#10B981',
              fontSize: 20,
              ml: 1,
              transition: 'transform 0.2s ease-in-out',
            }}
          />
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant='outlined'
            onClick={onToggleModules}
            disabled={loadingModules}
            sx={{
              borderColor: '#6B778C',
              color: '#1A1A1A',
              fontWeight: 'bold',
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              '&:hover': {
                borderColor: '#1A1A1A',
                bgcolor: '#F8F9FA',
              },
            }}
          >
            {loadingModules ? (
              <CircularProgress size={20} sx={{ color: '#1A1A1A' }} />
            ) : (
              'Módulos'
            )}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
const getTurmaUserStatus = (turma, matriculasList, turmasUsuario = []) => {
  const statusStr = String(turma.status || turma.nm_status || '').toLowerCase();
  if (
    statusStr === 'em breve' ||
    statusStr === 'indisponivel' ||
    statusStr === 'breve'
  ) {
    return 'EM_BREVE';
  }

  let matchedMatricula = null;
  if (Array.isArray(matriculasList)) {
    matchedMatricula = matriculasList.find(
      m =>
        String(m.id_turma) === String(turma.id_turma) ||
        String(m.turma_id) === String(turma.id_turma)
    );
  } else if (matriculasList) {
    const isThisTurmaInMatricula =
      String(matriculasList.id_turma) === String(turma.id_turma) ||
      String(matriculasList.turma_id) === String(turma.id_turma) ||
      !matriculasList.id_turma;
    if (isThisTurmaInMatricula) matchedMatricula = matriculasList;
  }

  const matriculaStatusUpper = String(
    turma.status_matricula ||
    turma.user_status ||
    (matchedMatricula ? matchedMatricula.status : '') ||
    ''
  ).toUpperCase();

  const isEnrolledInTurmasUsuario = turmasUsuario.some(
    u => String(u.id_turma) === String(turma.id_turma)
  );

  if (
    statusStr === 'fechada' ||
    statusStr === 'turma fechada' ||
    statusStr === 'inscrições encerradas' ||
    matriculaStatusUpper.includes('ENCERRADO') ||
    matriculaStatusUpper.includes('INSCRIÇÕES ENCERRADAS') ||
    matriculaStatusUpper.includes('CANCELADO') ||
    matriculaStatusUpper.includes('PERDIDO') ||
    matriculaStatusUpper === 'REJECTED' ||
    matriculaStatusUpper === 'EXPIRED' ||
    matriculaStatusUpper === 'FECHADA' ||
    matriculaStatusUpper === 'NAO_APROVADO'
  ) {
    return 'TURMA_FECHADA';
  }

  if (
    statusStr === 'em andamento' ||
    matriculaStatusUpper === 'EM_ANDAMENTO' ||
    matriculaStatusUpper === 'EM ANDAMENTO' ||
    (turma.inscrito && statusStr === 'em andamento')
  ) {
    return 'EM_ANDAMENTO';
  }

  if (
    statusStr === 'inscrito' ||
    isEnrolledInTurmasUsuario ||
    matriculaStatusUpper.includes('CONFIRMADA') ||
    matriculaStatusUpper.includes('MATRICULADO') ||
    matriculaStatusUpper === 'ACTIVE' ||
    matriculaStatusUpper === 'APPROVED' ||
    matriculaStatusUpper === 'CONFIRMADO' ||
    (turma.inscrito && (turma.confirmado || isEnrolledInTurmasUsuario))
  ) {
    return 'INSCRITO';
  }

  if (
    turma.inscrito ||
    matriculaStatusUpper.includes('AGUARDANDO') ||
    matriculaStatusUpper.includes('PRE_INSCRICAO') ||
    matriculaStatusUpper.includes('PRE-INSCRICAO') ||
    matriculaStatusUpper.includes('FILA') ||
    matriculaStatusUpper === 'PENDING' ||
    matriculaStatusUpper === 'EM_ANDAMENTO' ||
    matriculaStatusUpper === 'EM ANDAMENTO'
  ) {
    return 'EM_ANDAMENTO';
  }

  return 'INSCREVA_SE';
};

const TurmaCard = ({
  turma,
  matriculaStatus,
  turmasUsuario = [],
  enrollLoading = false,
  onEnroll,
}) => {
  const isCorporativa = turma.nm_descricao
    ?.toLowerCase()
    .includes('corporativa');

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('T')[0].split('-');
    const d = new Date(year, month - 1, day);
    return d
      .toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      .replace(' de ', ' ')
      .replace(' de ', ' ')
      .replace('.', '');
  };

  const rawModalidade = turma.sg_modalidade || (isCorporativa ? 'C' : 'R');
  const modalidadeLetter = rawModalidade.charAt(0).toUpperCase();
  const modMap = {
    H: 'Híbrida',
    R: 'Remota',
    C: 'Corporativa',
    P: 'Presencial',
  };
  const modalidadeCompleta = modMap[modalidadeLetter] || rawModalidade;

  const userStatus = getTurmaUserStatus(turma, matriculaStatus, turmasUsuario);

  const renderStatusButton = () => {
    switch (userStatus) {
      case 'EM_ANDAMENTO':
        return (
          <Button
            variant='contained'
            fullWidth
            disabled
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              bgcolor: '#0097A7 !important',
              color: '#FFFFFF !important',
              fontWeight: 700,
              py: 1,
              justifyContent: 'center',
              boxShadow: 'none',
            }}
          >
            Em andamento
          </Button>
        );

      case 'INSCRITO':
        return (
          <Button
            variant='contained'
            fullWidth
            disabled
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              bgcolor: '#E8F5E9 !important',
              color: '#2E7D32 !important',
              fontWeight: 700,
              py: 1,
              justifyContent: 'center',
              boxShadow: 'none',
            }}
          >
            Inscrito
          </Button>
        );

      case 'TURMA_FECHADA':
        return (
          <Button
            variant='contained'
            fullWidth
            disabled
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              bgcolor: '#F4F5F7 !important',
              color: '#6B778C !important',
              fontWeight: 700,
              py: 1,
              justifyContent: 'center',
              boxShadow: 'none',
            }}
          >
            Turma fechada
          </Button>
        );

      case 'EM_BREVE':
        return (
          <Button
            variant='outlined'
            fullWidth
            disabled
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              borderColor: '#EAEAEA !important',
              color: '#B0BEC5 !important',
              bgcolor: 'transparent !important',
              fontWeight: 700,
              py: 1,
              justifyContent: 'center',
            }}
          >
            Em breve
          </Button>
        );

      case 'INSCREVA_SE':
      default:
        return (
          <Button
            variant='outlined'
            fullWidth
            disabled={enrollLoading}
            onClick={() => !enrollLoading && onEnroll(turma)}
            endIcon={
              enrollLoading ? (
                <CircularProgress size={18} sx={{ color: '#0097A7' }} />
              ) : (
                <KeyboardArrowRight sx={{ color: '#0097A7' }} />
              )
            }
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              color: '#0097A7',
              borderColor: '#0097A7',
              fontWeight: 700,
              py: 1,
              justifyContent: 'center',
              '&:hover': {
                borderColor: '#00838F',
                bgcolor: 'rgba(0, 151, 167, 0.08)',
              },
              '&.Mui-disabled': {
                borderColor: 'rgba(0, 151, 167, 0.3)',
                color: 'rgba(0, 151, 167, 0.4)',
              },
            }}
          >
            Inscreva-se
          </Button>
        );
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: { xs: 320, sm: 420 },
        maxWidth: { xs: 320, sm: 420 },
        borderRadius: 3,
        border: '1px solid #EAEAEA',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            bgcolor: '#FFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #3366FF',
            flexShrink: 0,
          }}
        >
          <CalendarToday sx={{ color: '#3366FF', fontSize: 20 }} />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Typography
            variant='subtitle1'
            fontWeight='800'
            sx={{ color: '#0A2540', lineHeight: 1.3, pr: 1 }}
          >
            {turma.nm_descricao || `Turma ${turma.id_turma}`}
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: '16px',
              bgcolor: modalidadeCompleta === 'Híbrida' ? '#FFF3E0' : '#E6F0FF',
              color: modalidadeCompleta === 'Híbrida' ? '#E65100' : '#3366FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '800',
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {modalidadeCompleta}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          mb: 3,
        }}
      >
        {isCorporativa ? (
          <Typography
            variant='body2'
            sx={{ color: '#6B778C', fontWeight: 600 }}
          >
            Datas definidas pela organização
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday sx={{ color: '#6B778C', fontSize: 16 }} />
              <Typography
                variant='body2'
                sx={{ color: '#6B778C', fontWeight: 600, fontSize: '0.85rem' }}
              >
                {formatDate(turma.dt_inicio)} a {formatDate(turma.dt_termino)}
              </Typography>
            </Box>
            <Typography
              variant='body2'
              sx={{ color: '#6B778C', pl: 3.5, fontSize: '0.85rem' }}
            >
              Inscrições de {formatDate(turma.dt_inicio_inscricao)} até{' '}
              {formatDate(turma.dt_termino_inscricao)}
            </Typography>
          </>
        )}
      </Box>

      {renderStatusButton()}
    </Paper>
  );
};

TurmaCard.propTypes = {
  turma: PropTypes.object.isRequired,
  matriculaStatus: PropTypes.object,
  turmasUsuario: PropTypes.array,
  enrollLoading: PropTypes.bool,
  onEnroll: PropTypes.func.isRequired,
};

const SubscriptionStatusSection = ({
  statusData,
  turmasDisponiveis = [],
  courseOverview = null,
}) => {
  const navigate = useNavigate();

  const handleConsultar = () => {
    const el = document.getElementById('proximas-turmas');
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleComoFunciona = () => {
    const el = document.getElementById('como-funciona');
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  let title = 'Você ainda não está matriculado em nenhuma turma';
  let desc =
    'Esta plataforma é dedicada a uma única capacitação. Para iniciar sua jornada consulte as turmas disponíveis.';
  let image = ImgNoSubscribe;

  if (statusData) {
    const status = String(statusData.status || '').toUpperCase();
    let nomeCurso =
      statusData.nome_curso ||
      statusData.curso?.nm_titulo ||
      statusData.curso_nome ||
      statusData.turma?.curso?.nm_titulo ||
      '';
    let nomeTurma =
      statusData.nome_turma ||
      statusData.turma?.nm_descricao ||
      statusData.turma_nome ||
      '';
    let posicaoFila = statusData.posicao_fila || statusData.posicao || '';
    let dataLiberacao =
      statusData.data_liberacao ||
      statusData.dt_liberacao ||
      statusData.turma?.dt_liberacao ||
      '';
    let dtInicioRaw = statusData.dt_inicio || statusData.turma?.dt_inicio || '';

    // If dynamic info is missing, try to find it in turmasDisponiveis
    if (!nomeCurso || !nomeTurma || !dtInicioRaw) {
      const foundTurma = turmasDisponiveis.find(
        t =>
          String(t.id_turma) ===
          String(statusData.turma_id || statusData.id_turma)
      );
      if (foundTurma) {
        if (!nomeCurso)
          nomeCurso =
            foundTurma.curso_nome ||
            foundTurma.nm_curso ||
            foundTurma.curso?.nm_titulo ||
            '';
        if (!nomeTurma)
          nomeTurma =
            foundTurma.turma_nome ||
            foundTurma.nm_descricao ||
            foundTurma.nome_turma ||
            '';
        if (!dtInicioRaw) dtInicioRaw = foundTurma.dt_inicio || '';
      }
    }

    if (!nomeCurso && courseOverview) {
      nomeCurso =
        courseOverview.nm_titulo ||
        courseOverview.titulo ||
        courseOverview.nome_curso ||
        'Capacitação para a operação e manutenção de reatores Fischer-Tropsch (FT) via rota PBtL';
    } else if (!nomeCurso) {
      nomeCurso =
        'Capacitação para a operação e manutenção de reatores Fischer-Tropsch (FT) via rota PBtL';
    }

    if (!nomeTurma) {
      const tid = statusData.turma_id || statusData.id_turma;
      nomeTurma = tid ? `Turma ${tid}` : '';
    }

    if (
      status.includes('AGUARDANDO') ||
      status.includes('PRE_INSCRICAO') ||
      status.includes('PRE-INSCRICAO') ||
      status === 'PENDING' ||
      status === 'CONVOCADO'
    ) {
      title = 'Sua pré-inscrição foi realizada, aguardando confirmação';
      desc =
        'Enviamos um e-mail de confirmação. Confirme sua pré-inscrição em até 48 horas após o recebimento da mensagem para garantir sua vaga.';
      image = ImgInscricaoRealizada;
    } else if (status.includes('FILA')) {
      title = 'Você está na fila de espera';
      desc = `Sua pré-inscrição no curso ${nomeCurso} é a ${posicaoFila}ª na fila de espera. Caso uma vaga seja liberada, você receberá um aviso para confirmar seu interesse dentro do prazo informado.`;
      image = ImgFilaEspera;
    } else if (
      status.includes('ENCERRADO') ||
      status.includes('CANCELADO') ||
      status.includes('PERDIDO') ||
      status === 'REJECTED' ||
      status === 'EXPIRED' ||
      status === 'NAO_APROVADO' ||
      status === 'NAO APROVADO'
    ) {
      title = 'Prazo encerrado, vaga não confirmada';
      desc =
        'O período para confirmar sua participação foi encerrado e, como não houve confirmação dentro do prazo, a vaga foi disponibilizada para outro participante.';
      image = ImgPrazoEncerrado;
    } else {
      let displayDate = dataLiberacao;
      if (!displayDate && dtInicioRaw) {
        try {
          const [year, month, day] = dtInicioRaw.split('T')[0].split('-');
          const d = new Date(year, month - 1, day);
          displayDate = d.toLocaleDateString('pt-BR');
        } catch (e) {
          displayDate = dtInicioRaw;
        }
      }

      title = 'Sua inscrição está confirmada';
      desc = `Sua vaga no curso ${nomeCurso}, turma ${nomeTurma}, está garantida. O conteúdo será liberado em ${displayDate || 'breve'}. Até lá, nenhuma ação é necessária.`;
      image = ImgVagaConfirmada;
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid #EAEAEA',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        mb: 8,
        bgcolor: '#FDFCFD',
      }}
    >
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
          p: { xs: 3, md: 6 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          component='img'
          src={image}
          alt={title}
          sx={{
            maxWidth: '100%',
            height: 'auto',
            objectFit: 'contain',
          }}
        />
      </Box>
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
          p: { xs: 3, md: 6 },
          pl: { md: 0 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography
          variant='h4'
          fontWeight='800'
          sx={{ color: '#0A2540', lineHeight: 1.2 }}
        >
          {title}
        </Typography>
        <Typography variant='body1' sx={{ color: '#6B778C', mb: 2 }}>
          {desc}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant='contained'
            endIcon={<KeyboardArrowRight />}
            sx={{
              bgcolor: '#0A2540',
              color: '#fff',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3,
              py: 1.5,
              '&:hover': { bgcolor: '#001b33' },
            }}
            onClick={handleConsultar}
          >
            Consultar turmas
          </Button>
          <Button
            variant='outlined'
            sx={{
              borderColor: '#0A2540',
              color: '#0A2540',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3,
              py: 1.5,
              '&:hover': {
                borderColor: '#001b33',
                bgcolor: 'rgba(10, 37, 64, 0.04)',
              },
            }}
            onClick={handleComoFunciona}
          >
            Como funciona a capacitação
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
SubscriptionStatusSection.propTypes = {
  statusData: PropTypes.object,
};

const CourseOverviewSection = ({ course, modulesInfo }) => {
  const navigate = useNavigate();
  const [modulesExpanded, setModulesExpanded] = useState(false);

  const handleToggleModules = () => {
    const isExpanding = !modulesExpanded;
    setModulesExpanded(isExpanding);

    if (isExpanding) {
      setTimeout(() => {
        const element = document.getElementById('overview-modules-grid');
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 150);
    }
  };

  if (!course) return null;

  const title =
    course.nm_titulo ||
    'Capacitação para a operação e manutenção de reatores Fischer-Tropsch (FT) via rota PBtL';
  const desc =
    course.nm_descricao ||
    'Formação técnica aplicada sobre operação, manutenção, segurança e navegação em ambiente imersivo para reatores Fischer-Tropsch.';
  const versao = course.versoes && course.versoes[0] ? course.versoes[0] : {};
  const modMap = { R: 'Remoto', H: 'Híbrido' };
  const modalidade =
    modMap[versao.sg_modalidade] || versao.sg_modalidade || 'Remota / Híbrida';
  const cargaHoraria = versao.nr_carga_horaria
    ? `${versao.nr_carga_horaria} horas`
    : '40 horas';

  const uniqueModules = [];
  if (modulesInfo?.modulos) {
    const seenTitles = new Set();
    modulesInfo.modulos.forEach(mod => {
      if (!seenTitles.has(mod.nm_titulo)) {
        seenTitles.add(mod.nm_titulo);
        uniqueModules.push(mod);
      }
    });
  }
  const totalModulos =
    uniqueModules.length > 0 ? uniqueModules.length : modulesInfo?.total || 6;
  const imagem = course.imagem_url || ImgCapaCurso;

  return (
    <Box sx={{ mb: 8 }}>
      <Typography
        variant='h4'
        fontWeight='800'
        sx={{ color: '#0A2540', mb: 3 }}
      >
        Conheça a capacitação
      </Typography>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid #EAEAEA',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: '45%' },
            position: 'relative',
          }}
        >
          <Box
            component='img'
            src={imagem}
            alt={title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              minHeight: 250,
            }}
          />
        </Box>
        <Box
          sx={{
            width: { xs: '100%', md: '55%' },
            p: 4,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant='h5'
            fontWeight='800'
            sx={{ color: '#0A2540', mb: 2, lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          <Typography variant='body1' sx={{ color: '#6B778C', mb: 4 }}>
            {desc}
          </Typography>

          {/* Info grid */}
          <Box
            sx={{
              display: 'flex',
              border: '1px solid #EAEAEA',
              borderRadius: 2,
              p: 2,
              mb: 4,
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            {/* Feature 1 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#E3FCEF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ComputerOutlined sx={{ color: '#00875A', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  variant='caption'
                  sx={{
                    color: '#00875A',
                    fontWeight: 'bold',
                    display: 'block',
                  }}
                >
                  Modalidade
                </Typography>
                <Typography variant='body2' sx={{ color: '#6B778C' }}>
                  {modalidade}
                </Typography>
              </Box>
            </Box>
            <Divider
              orientation='vertical'
              flexItem
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
            {/* Feature 2 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#E3FCEF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AccessTime sx={{ color: '#00875A', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  variant='caption'
                  sx={{
                    color: '#00875A',
                    fontWeight: 'bold',
                    display: 'block',
                  }}
                >
                  Carga horária
                </Typography>
                <Typography variant='body2' sx={{ color: '#6B778C' }}>
                  {cargaHoraria}
                </Typography>
              </Box>
            </Box>
            <Divider
              orientation='vertical'
              flexItem
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
            {/* Feature 3 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#E3FCEF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AssignmentOutlined sx={{ color: '#00875A', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  variant='caption'
                  sx={{
                    color: '#00875A',
                    fontWeight: 'bold',
                    display: 'block',
                  }}
                >
                  Estrutura
                </Typography>
                <Typography variant='body2' sx={{ color: '#6B778C' }}>
                  {totalModulos} módulos
                </Typography>
              </Box>
            </Box>
            <Divider
              orientation='vertical'
              flexItem
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
            {/* Feature 4 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#E3FCEF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CardMembershipOutlined
                  sx={{ color: '#00875A', fontSize: 20 }}
                />
              </Box>
              <Box>
                <Typography
                  variant='caption'
                  sx={{
                    color: '#00875A',
                    fontWeight: 'bold',
                    display: 'block',
                  }}
                >
                  Certificado
                </Typography>
                <Typography variant='body2' sx={{ color: '#6B778C' }}>
                  Disponível ao concluir
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant='outlined'
              onClick={handleToggleModules}
              sx={{
                borderColor: '#6B778C',
                color: '#1A1A1A',
                fontWeight: 'bold',
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  borderColor: '#1A1A1A',
                  bgcolor: '#F8F9FA',
                },
              }}
            >
              Ver estrutura do curso
            </Button>
          </Box>
        </Box>
      </Paper>

      {modulesExpanded && uniqueModules.length > 0 && (
        <Grid
          container
          spacing={2}
          alignItems='stretch'
          sx={{ mt: 4 }}
          id='overview-modules-grid'
        >
          {uniqueModules.map((modulo, idx) => {
            const modData = {
              numero: modulo.nr_ordem !== undefined ? modulo.nr_ordem : idx + 1,
              titulo: modulo.nm_titulo,
              descricao: modulo.nm_descricao,
              duracao: modulo.nr_carga_horaria
                ? typeof modulo.nr_carga_horaria === 'number' &&
                  modulo.nr_carga_horaria >= 60
                  ? formatModuloDuracao(modulo.nr_carga_horaria)
                  : `${modulo.nr_carga_horaria} horas`
                : undefined,
              status: 'locked',
            };
            return (
              <Grid
                item
                key={idx}
                sx={{
                  display: 'flex',
                  flex: '0 0 32.333333%',
                  maxWidth: '32.333333%',
                  justifyContent: 'center',
                }}
              >
                <ModuleCard
                  modulo={modData}
                  readOnly={true}
                  onClick={() => {
                    const cursoId = course?.id_curso || course?.id;
                    if (cursoId) {
                      navigate(`/curso/${cursoId}/conteudo?module=${idx}`);
                    }
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};
CourseOverviewSection.propTypes = {
  course: PropTypes.object,
  modulesInfo: PropTypes.object,
};

const CursosDisponiveisPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [turmasUsuario, setTurmasUsuario] = useState([]);
  const [turmasDisponiveis, setTurmasDisponiveis] = useState([]);
  const [matriculaStatus, setMatriculaStatus] = useState(null);
  const [courseOverview, setCourseOverview] = useState(null);
  const [courseModulesInfo, setCourseModulesInfo] = useState(null);
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModulesByCourse, setExpandedModulesByCourse] = useState({});
  const [confirmEnrollTurma, setConfirmEnrollTurma] = useState(null);
  const [activeTurmaTab, setActiveTurmaTab] = useState(0);

  const faqData = [
    {
      question: 'Como acompanho meu progresso?',
      answer:
        'Na página inicial (Home), você pode acompanhar o percentual concluído, os módulos finalizados e a próxima atividade disponível. O progresso é atualizado conforme você conclui as aulas, práticas e avaliações da trilha.',
    },
    {
      question: 'Por que um módulo está bloqueado?',
      answer:
        'Os módulos seguem uma sequência de aprendizagem. Para liberar a próxima etapa, conclua os conteúdos, atividades e avaliações obrigatórias do módulo anterior. Na modalidade híbrida, os módulos presenciais também dependem da conclusão dos módulos teóricos iniciais e do registro de presença pelo professor.',
    },
    {
      question: 'Como acesso o Ambiente Virtual 3D?',
      answer:
        'Acesse o módulo e o conteúdo correspondentes e selecione a atividade prática indicada. Antes do primeiro acesso, recomendamos realizar o Módulo 0, que apresenta os comandos e orienta a navegação no ambiente virtual.',
    },
    {
      question: 'Quantas tentativas tenho nas avaliações?',
      answer:
        'Você terá até três tentativas para alcançar a nota mínima exigida em cada avaliação modular.',
    },
    {
      question: 'Quais são os critérios para receber o certificado?',
      answer:
        'Para receber o certificado, você deve concluir toda a trilha de conteúdos e atingir a nota mínima em todas as avaliações exigidas no curso da sua turma. Essas informações referentes as avaliações podem ser visualizadas antes do início de cada avaliação, juntamente com o tempo máximo de execução.',
    },
    {
      question:
        'O que devo fazer se a plataforma ou o simulador apresentar um problema?',
      answer:
        'Atualize a página e verifique sua conexão com a internet. Caso o problema persista, registre uma captura de tela ou uma foto da mensagem exibida e entre em contato com o suporte, informando a atividade ou página acessada, o módulo em que o problema ocorreu, o dispositivo e o navegador utilizados, além de uma breve descrição do erro.',
    },
  ];

  const handleContinueCourse = async (cursoId, moduloIndex = null) => {
    try {
      if (moduloIndex !== null && moduloIndex !== undefined) {
        navigate(`/curso/${cursoId}/conteudo?module=${moduloIndex}`);
      } else {
        navigate(`/curso/${cursoId}/conteudo`);
      }
    } catch {
      // Ignore
    }
  };

  const handleToggleCourseModules = cursoId => {
    const isExpanding = !expandedModulesByCourse[cursoId];

    setExpandedModulesByCourse(prev => ({
      ...prev,
      [cursoId]: !prev[cursoId],
    }));

    if (isExpanding) {
      setTimeout(() => {
        const element = document.getElementById(`modules-grid-${cursoId}`);
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 150);
    }
  };

  const mapEnrolledCourses = useCallback(async (enrolledList, turmasDisp) => {
    return Promise.all(
      enrolledList.map(async item => {
        let started = false;
        let progressInfo = null;
        let finalPorcentagem = Number(item.progresso || 0);
        let modulosTotal = item.modulos_total || item.total_modulos || 0;
        let modulosConcluidos = 0;
        let concluido = false;
        let currentConteudoTitle = 'Continuar Capacitação';
        let currentModuloTitle = 'Módulo 1';
        let rawModulos = [];

        try {
          progressInfo = await getTrilhaAcompanhamento(item.id_curso_usuario);

          finalPorcentagem = Number(progressInfo.progresso_geral || 0);
          modulosTotal =
            progressInfo.modulos_total ??
            progressInfo.total_modulos ??
            modulosTotal;
          modulosConcluidos = Number(progressInfo.modulos_concluidos || 0);
          started = !!(
            progressInfo.iniciado ||
            finalPorcentagem > 0 ||
            item.dh_inicio ||
            item.progresso_geral > 0 ||
            item.progresso > 0
          );
          concluido = !!progressInfo.concluido;

          const proxima = progressInfo.proxima_atividade;
          if (proxima) {
            currentConteudoTitle = proxima.titulo || currentConteudoTitle;
            const mod = proxima.modulo;
            if (mod) {
              currentModuloTitle =
                mod.titulo ||
                (mod.numero !== undefined && mod.numero !== null
                  ? `Módulo ${mod.numero}`
                  : currentModuloTitle);
            }
          }

          rawModulos = mapModuloPathToCards(progressInfo.modulo_path || []);
        } catch (err) {
          started = !!(
            item.dh_inicio ||
            item.progresso_geral > 0 ||
            item.progresso > 0
          );
        }

        return {
          id: item.id_curso_usuario,
          id_turma: item.id_turma,
          imagem:
            item.curso?.image ||
            'https://source.unsplash.com/random/800x600/?technology',
          titulo: item.curso?.nm_titulo || 'Turma Ativa',
          descricao:
            item.curso?.nm_descricao || 'Descrição da turma não disponível.',
          duracao: modulosTotal + ' módulos',
          modalidade:
            turmasDisp.find(t => String(t.id_turma) === String(item.id_turma))
              ?.sg_modalidade ||
            item.sg_modalidade ||
            item.curso?.modalidade ||
            'Remota',
          progress: {
            porcentagem_concluida: finalPorcentagem,
          },
          aulas: modulosTotal,
          modulos_total: modulosTotal,
          modulos_concluidos: modulosConcluidos,
          started,
          concluido,
          turma_nome:
            turmasDisp.find(t => String(t.id_turma) === String(item.id_turma))
              ?.nm_descricao ||
            item.curso?.tags ||
            'Turma Ativa',
          currentConteudoTitle,
          currentModuloTitle,
          raw_modulos: rawModulos,
        };
      })
    );
  }, []);

  const loadData = useCallback(
    async ({ showLoading = true } = {}) => {
      try {
        if (showLoading) setLoading(true);
        const [
          turmasDispRes,
          enrolledData,
          matriculaRes,
          cursosRes,
          modulosRes,
        ] = await Promise.all([
          getTurmas(),
          getUserCourses(),
          getStatusMatricula().catch(() => null),
          getCourses().catch(() => null),
          getCourseModulesAdmin(null, '', 1, 100).catch(() => null),
        ]);

        if (cursosRes && cursosRes.cursos && cursosRes.cursos.length > 0) {
          setCourseOverview(cursosRes.cursos[0]);
        }
        if (modulosRes && modulosRes.total !== undefined) {
          setCourseModulesInfo(modulosRes);
        }

        if (matriculaRes) {
          if (Array.isArray(matriculaRes)) {
            setMatriculaStatus(matriculaRes);
          } else if (
            typeof matriculaRes === 'object' &&
            Object.keys(matriculaRes).length > 0
          ) {
            setMatriculaStatus([matriculaRes]);
          }
        }

        const turmasDisp = Array.isArray(turmasDispRes)
          ? turmasDispRes
          : turmasDispRes.turmas || turmasDispRes.data || [];

        const enrolledList = Array.isArray(enrolledData)
          ? enrolledData
          : enrolledData.cursos_usuario || enrolledData.data || [];

        const mappedEnrolled = await mapEnrolledCourses(
          enrolledList,
          turmasDisp
        );

        setTurmasDisponiveis(turmasDisp);
        setTurmasUsuario(mappedEnrolled);
      } catch (err) {
        console.error('Erro ao carregar turmas:', err);
        setError('Erro ao carregar turmas disponíveis e matriculadas.');
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [mapEnrolledCourses]
  );

  // Refetch whenever this route is visited (e.g. back from /curso/.../conteudo)
  useEffect(() => {
    loadData({ showLoading: true });
  }, [location.key, loadData]);

  // Soft refetch when tab becomes visible again (covers bfcache / delayed BE progress)
  useEffect(() => {
    const refreshProgress = () => {
      if (document.visibilityState === 'visible') {
        loadData({ showLoading: false });
      }
    };
    const onPageShow = event => {
      if (event.persisted) {
        loadData({ showLoading: false });
      }
    };
    document.addEventListener('visibilitychange', refreshProgress);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', refreshProgress);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [loadData]);

  const handleEnrollClick = turma => {
    if (enrollLoading) return;
    setConfirmEnrollTurma(turma);
  };

  useEffect(() => {
    if (window.location.hash === '#meus-cursos' && turmasUsuario.length > 0) {
      const firstCursoId = turmasUsuario[0].id;
      if (!expandedModulesByCourse[firstCursoId]) {
        handleToggleCourseModules(firstCursoId);
      }

      const element = document.getElementById('meus-cursos');
      if (element) {
        setTimeout(() => {
          const y = element.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 100);
      }
    }
  }, [
    window.location.hash,
    turmasUsuario,
    expandedModulesByCourse,
    handleToggleCourseModules,
  ]);

  const handleConfirmEnroll = async () => {
    if (!confirmEnrollTurma || enrollLoading) return;
    const idTurma = confirmEnrollTurma.id_turma;
    try {
      setEnrollLoading(true);
      const currentUser = getCurrentUser();
      await inscreverUsuarioTurma(idTurma, currentUser.id_usuario);
      setSuccessMessage('Inscrição realizada com sucesso!');
      setConfirmEnrollTurma(null);

      const [turmasDispRes, turmasUsuRes, matriculaRes] = await Promise.all([
        getTurmas(),
        getUserCourses(),
        getStatusMatricula().catch(() => null),
      ]);

      if (matriculaRes) {
        if (Array.isArray(matriculaRes)) {
          setMatriculaStatus(matriculaRes);
        } else if (
          typeof matriculaRes === 'object' &&
          Object.keys(matriculaRes).length > 0
        ) {
          setMatriculaStatus([matriculaRes]);
        }
      }

      setTurmasDisponiveis(
        Array.isArray(turmasDispRes)
          ? turmasDispRes
          : turmasDispRes.turmas || turmasDispRes.data || []
      );
      setTurmasUsuario(
        Array.isArray(turmasUsuRes)
          ? turmasUsuRes
          : turmasUsuRes.cursos_usuario || turmasUsuRes.data || []
      );

      window.setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Erro ao se inscrever:', err);
      window.alert('Erro ao realizar inscrição.');
      setConfirmEnrollTurma(null);
    } finally {
      setEnrollLoading(false);
    }
  };

  const handleContinuarAula = cursoId => {
    navigate(`/curso/${cursoId}/conteudo`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#021b1f',
        }}
      >
        <CircularProgress sx={{ color: '#FFB74D' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#FAFAFA', minHeight: '100vh' }}>
      {/* Banner - Full Width */}
      <CarouselBanner
        username={
          getCurrentUser()?.nm_usuario || getCurrentUser()?.name || 'Usuário'
        }
        isEnrolled={turmasUsuario && turmasUsuario.length > 0}
        nextTurma={turmasDisponiveis.length > 0 ? turmasDisponiveis[0] : null}
      />

      {/* Main Content */}
      <Container maxWidth='xl' sx={{ py: 8 }}>
        {/* Status Banners */}
        {(() => {
          let bannersToRender = [];

          if (Array.isArray(matriculaStatus)) {
            bannersToRender = matriculaStatus.filter(
              m =>
                !turmasUsuario.some(
                  t => String(t.id_turma) === String(m.turma_id || m.id_turma)
                ) && m.status
            );
          } else if (matriculaStatus && matriculaStatus.status) {
            const alreadyOngoing = turmasUsuario.some(
              t =>
                String(t.id_turma) ===
                String(matriculaStatus.turma_id || matriculaStatus.id_turma)
            );
            if (!alreadyOngoing) {
              bannersToRender = [matriculaStatus];
            }
          }

          if (bannersToRender.length === 0 && turmasUsuario.length === 0) {
            return <SubscriptionStatusSection statusData={null} />;
          }

          return bannersToRender.map((m, idx) => (
            <SubscriptionStatusSection
              key={m.turma_id || m.id_turma || idx}
              statusData={m}
              turmasDisponiveis={turmasDisponiveis}
              courseOverview={courseOverview}
            />
          ));
        })()}

        {/* Not Subscribed / Course Overview Section */}
        {turmasUsuario.length === 0 && (
          <Box id='sobre-curso'>
            <CourseOverviewSection
              course={courseOverview}
              modulesInfo={courseModulesInfo}
            />
          </Box>
        )}

        {/* Sua turma em andamento Section */}
        {turmasUsuario.length > 0 && (
          <Box
            id='meus-cursos'
            sx={{
              mb: 8,
            }}
          >
            <Typography
              variant='h4'
              fontWeight='800'
              sx={{ color: '#0A2540', mb: 3 }}
            >
              {turmasUsuario[0]?.progress?.porcentagem_concluida === 100 ||
                turmasUsuario[0]?.concluido
                ? 'Seu curso concluído'
                : 'Seu curso em andamento'}
            </Typography>

            {successMessage && (
              <Alert severity='success' sx={{ mb: 4 }}>
                {successMessage}
              </Alert>
            )}

            {turmasUsuario.length > 1 && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={activeTurmaTab}
                  onChange={(e, newValue) => setActiveTurmaTab(newValue)}
                  variant='scrollable'
                  scrollButtons='auto'
                  aria-label='turmas em andamento'
                >
                  {turmasUsuario.map((cursoMock, index) => (
                    <Tab
                      key={cursoMock.id}
                      label={cursoMock.turma_nome || `Turma ${index + 1}`}
                    />
                  ))}
                </Tabs>
              </Box>
            )}

            {/* Course Card mapping */}
            {turmasUsuario.map((cursoMock, index) => {
              if (index !== activeTurmaTab) return null;

              const cursoId = cursoMock.id;
              const modulos = cursoMock.raw_modulos;
              const isExpanded = expandedModulesByCourse[cursoId];

              return (
                <Box key={cursoId}>
                  <CourseCard
                    curso={cursoMock}
                    onContinue={() => handleContinueCourse(cursoId)}
                    onToggleModules={() => handleToggleCourseModules(cursoId)}
                    modulesExpanded={isExpanded || false}
                    modulos={modulos}
                    loadingModules={false}
                  />

                  {isExpanded && modulos && (
                    <Grid
                      id={`modules-grid-${cursoId}`}
                      container
                      spacing={2}
                      alignItems='stretch'
                      sx={{ mt: 3, mb: 6 }}
                    >
                      {modulos.map((modulo, idx) => (
                        <Grid
                          item
                          key={idx}
                          sx={{
                            display: 'flex',
                            flex: '0 0 32.333333%',
                            maxWidth: '32.333333%',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 1,
                          }}
                        >
                          <ModuleCard
                            modulo={modulo}
                            onClick={() =>
                              handleContinueCourse(
                                cursoId,
                                modulo.numero !== undefined
                                  ? modulo.numero
                                  : idx
                              )
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Próximas turmas Section */}
        {(() => {
          const proximasTurmas = turmasDisponiveis.filter(turma => {
            if (!turma.dt_termino) return true;
            const [year, month, day] = turma.dt_termino
              .split('T')[0]
              .split('-');
            const dataTermino = new Date(year, month - 1, day);
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            return dataTermino >= hoje;
          });

          if (proximasTurmas.length === 0) return null;

          return (
            <Box sx={{ mb: 8 }} id='proximas-turmas'>
              <Typography
                variant='h4'
                fontWeight='800'
                sx={{ color: '#0A2540', mb: 3 }}
              >
                Próximas turmas
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  overflowX: 'auto',
                  pb: 2,
                  '&::-webkit-scrollbar': { height: 8 },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#ccc',
                    borderRadius: 4,
                  },
                }}
              >
                {proximasTurmas.map(turma => (
                  <TurmaCard
                    key={turma.id_turma}
                    turma={turma}
                    matriculaStatus={matriculaStatus}
                    turmasUsuario={turmasUsuario}
                    enrollLoading={enrollLoading}
                    onEnroll={handleEnrollClick}
                  />
                ))}
              </Box>
            </Box>
          );
        })()}

        {/* Como Funciona Section */}
        <Box id='como-funciona' sx={{ mb: 8 }}>
          <Typography
            variant='h4'
            fontWeight='800'
            sx={{ color: '#0A2540', mb: 3 }}
          >
            {turmasUsuario && turmasUsuario.length > 0
              ? 'Como concluir sua capacitação'
              : 'Realize sua inscrição'}
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: '16px',
              border: '1px solid #EAEAEA',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 4, md: 2 },
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              bgcolor: '#fff',
              position: 'relative',
            }}
          >
            {turmasUsuario && turmasUsuario.length > 0 ? (
              <>
                {/* Step 1 */}
                <Box sx={{ flex: 1, position: 'relative', px: { md: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: '#0A2540',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        1
                      </Box>
                      <Typography
                        variant='subtitle1'
                        fontWeight='800'
                        sx={{ color: '#0A2540', lineHeight: 1.2 }}
                      >
                        Acesse o curso
                      </Typography>
                    </Box>
                    <img
                      src={IconeTrilha1}
                      alt='Acesse o curso'
                      style={{
                        width: '64px',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#637381',
                        lineHeight: 1.5,
                        fontSize: '0.85rem',
                      }}
                    >
                      Entre na página do conteúdo do curso e consulte os módulos
                      disponíveis. Recomendamos iniciar pelo Módulo 0, que
                      apresenta a plataforma e a navegação no gêmeo digital.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      right: -20,
                      top: '40%',
                      transform: 'translateY(-50%)',
                      color: '#DFE3E8',
                    }}
                  >
                    <KeyboardArrowRight />
                  </Box>
                </Box>

                {/* Step 2 */}
                <Box sx={{ flex: 1, position: 'relative', px: { md: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: '#0A2540',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        2
                      </Box>
                      <Typography
                        variant='subtitle1'
                        fontWeight='800'
                        sx={{ color: '#0A2540', lineHeight: 1.2 }}
                      >
                        Assista aulas
                      </Typography>
                    </Box>
                    <img
                      src={IconeTrilha2}
                      alt='Assista aulas'
                      style={{
                        width: '64px',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#637381',
                        lineHeight: 1.5,
                        fontSize: '0.85rem',
                      }}
                    >
                      Acompanhe as videoaulas e consulte os materiais de apoio
                      de cada módulo. Os conteúdos apresentam os fundamentos
                      técnicos necessários para as atividades práticas.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      right: -20,
                      top: '40%',
                      transform: 'translateY(-50%)',
                      color: '#DFE3E8',
                    }}
                  >
                    <KeyboardArrowRight />
                  </Box>
                </Box>

                {/* Step 3 */}
                <Box sx={{ flex: 1, position: 'relative', px: { md: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: '#0A2540',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        3
                      </Box>
                      <Typography
                        variant='subtitle1'
                        fontWeight='800'
                        sx={{ color: '#0A2540', lineHeight: 1.2 }}
                      >
                        Realize atividades
                      </Typography>
                    </Box>
                    <img
                      src={IconeTrilha3}
                      alt='Realize atividades'
                      style={{
                        width: '64px',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#637381',
                        lineHeight: 1.5,
                        fontSize: '0.85rem',
                      }}
                    >
                      Execute as práticas imersivas no simulador e responda às
                      avaliações ao final de cada módulo. Conclua cada etapa
                      para liberar o módulo seguinte e avançar na capacitação.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      right: -20,
                      top: '40%',
                      transform: 'translateY(-50%)',
                      color: '#DFE3E8',
                    }}
                  >
                    <KeyboardArrowRight />
                  </Box>
                </Box>

                {/* Step 4 */}
                <Box sx={{ flex: 1, px: { md: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: '#0A2540',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        4
                      </Box>
                      <Typography
                        variant='subtitle1'
                        fontWeight='800'
                        sx={{ color: '#0A2540', lineHeight: 1.2 }}
                      >
                        Emita o certificado
                      </Typography>
                    </Box>
                    <img
                      src={IconeTrilha4}
                      alt='Emita o certificado'
                      style={{
                        width: '64px',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#637381',
                        lineHeight: 1.5,
                        fontSize: '0.85rem',
                      }}
                    >
                      Conclua todos os módulos e alcance os critérios mínimos de
                      aprovação nas avaliações. Após a validação da sua
                      conclusão, o certificado ficará disponível na plataforma
                      para emissão.
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : (
              <>
                {/* Step 1 */}
                <Box sx={{ flex: 1, position: 'relative', px: { md: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: '#0A2540',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        1
                      </Box>
                      <Typography
                        variant='subtitle1'
                        fontWeight='800'
                        sx={{ color: '#0A2540', lineHeight: 1.2 }}
                      >
                        Inscreva-se na Turma
                      </Typography>
                    </Box>
                    <img
                      src={IconeInscricao1}
                      alt='Inscreva-se na Turma'
                      style={{
                        width: '64px',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#637381',
                        lineHeight: 1.5,
                        fontSize: '0.85rem',
                      }}
                    >
                      Consulte as turmas disponíveis e verifique informações
                      como modalidade, período de realização e prazo de
                      inscrição. Em seguida, selecione a turma mais adequada à
                      sua disponibilidade.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      right: -20,
                      top: '40%',
                      transform: 'translateY(-50%)',
                      color: '#DFE3E8',
                    }}
                  >
                    <KeyboardArrowRight />
                  </Box>
                </Box>

                {/* Step 2 */}
                <Box sx={{ flex: 1, position: 'relative', px: { md: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: '#0A2540',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        2
                      </Box>
                      <Typography
                        variant='subtitle1'
                        fontWeight='800'
                        sx={{ color: '#0A2540', lineHeight: 1.2 }}
                      >
                        Solicite sua participação
                      </Typography>
                    </Box>
                    <img
                      src={IconeInscricao2}
                      alt='Solicite sua participação'
                      style={{
                        width: '64px',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#637381',
                        lineHeight: 1.5,
                        fontSize: '0.85rem',
                      }}
                    >
                      Após selecionar a turma, sua inscrição será registrada
                      automaticamente. Você receberá um e-mail com a solicitação
                      de participação e as informações sobre sua situação na
                      turma.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      right: -20,
                      top: '40%',
                      transform: 'translateY(-50%)',
                      color: '#DFE3E8',
                    }}
                  >
                    <KeyboardArrowRight />
                  </Box>
                </Box>

                {/* Step 3 */}
                <Box sx={{ flex: 1, position: 'relative', px: { md: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: '#0A2540',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        3
                      </Box>
                      <Typography
                        variant='subtitle1'
                        fontWeight='800'
                        sx={{ color: '#0A2540', lineHeight: 1.2 }}
                      >
                        Acompanhe seu status
                      </Typography>
                    </Box>
                    <img
                      src={IconeInscricao3}
                      alt='Acompanhe seu status'
                      style={{
                        width: '64px',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#637381',
                        lineHeight: 1.5,
                        fontSize: '0.85rem',
                      }}
                    >
                      Fique atento ao seu e-mail. Na semana anterior ao início
                      da turma, os participantes convocados receberão um e-mail
                      para confirmar definitivamente a vaga.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      right: -20,
                      top: '40%',
                      transform: 'translateY(-50%)',
                      color: '#DFE3E8',
                    }}
                  >
                    <KeyboardArrowRight />
                  </Box>
                </Box>

                {/* Step 4 */}
                <Box sx={{ flex: 1, px: { md: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: '#0A2540',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        4
                      </Box>
                      <Typography
                        variant='subtitle1'
                        fontWeight='800'
                        sx={{ color: '#0A2540', lineHeight: 1.2 }}
                      >
                        Confirme sua vaga
                      </Typography>
                    </Box>
                    <img
                      src={IconeInscricao4}
                      alt='Confirme sua vaga'
                      style={{
                        width: '64px',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#637381',
                        lineHeight: 1.5,
                        fontSize: '0.85rem',
                      }}
                    >
                      Ao receber o e-mail confirmação definitiva, confirme sua
                      participação pelo link enviado em até 48 horas. Após a
                      confirmação, sua turma será liberada na página inicial.
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Box>

        {/* Precisa de ajuda? Banner */}
        <Box
          id='ajuda'
          sx={{
            mb: 8,
            bgcolor: '#042235',
            borderRadius: '16px',
            p: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background texture simulating the dotted gradient */}
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '40%',
              background:
                'radial-gradient(circle at 100% 50%, rgba(0,151,167,0.2) 0%, transparent 60%)',
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 3, zIndex: 1 }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HeadsetMicOutlined sx={{ color: '#fff', fontSize: 32 }} />
            </Box>
            <Box>
              <Typography
                variant='h5'
                fontWeight='800'
                sx={{ color: '#fff', mb: 0.5 }}
              >
                Precisa de ajuda?
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: '#B0C4DF', fontSize: '0.9rem' }}
              >
                Nossa equipe está pronta para apoiar você em cada etapa da sua
                jornada.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              zIndex: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Button
              variant='contained'
              endIcon={<Launch />}
              onClick={() => navigate('/central-ajuda')}
              sx={{
                bgcolor: '#0097A7',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#00838F', boxShadow: 'none' },
              }}
            >
              Central de ajuda
            </Button>
            <Button
              variant='outlined'
              component='a'
              href='mailto:suportesaf@4controlgroup.com'
              endIcon={<ChatBubbleOutline />}
              sx={{
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,0.05)',
                },
              }}
            >
              Falar com o suporte
            </Button>
          </Box>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant='h4'
            fontWeight='800'
            sx={{ color: '#0A2540', mb: 3 }}
          >
            Dúvidas frequentes
          </Typography>
          <Typography variant='body2' sx={{ color: '#6B778C', mb: 3 }}>
            Respostas rápidas para as principais dúvidas da sua turma.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #EAEAEA',
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            {faqData.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                disableGutters
                square
                sx={{
                  borderTop: index === 0 ? 'none' : '1px solid #EAEAEA',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<KeyboardArrowDown />}
                  sx={{
                    '& .MuiAccordionSummary-content': { my: 2 },
                    bgcolor: '#fff',
                  }}
                >
                  <Typography fontWeight='700' sx={{ color: '#0A2540' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    bgcolor: '#F8F9FA',
                    p: 3,
                    borderTop: '1px solid #EAEAEA',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{ color: '#4A5568', lineHeight: 1.6 }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      </Container>

      {/* Confirmation Dialog */}
      <Dialog
        open={Boolean(confirmEnrollTurma)}
        onClose={() => !enrollLoading && setConfirmEnrollTurma(null)}
        aria-labelledby='confirm-enroll-title'
        aria-describedby='confirm-enroll-description'
      >
        <DialogTitle id='confirm-enroll-title'>Confirmar inscrição</DialogTitle>
        <DialogContent>
          <DialogContentText id='confirm-enroll-description'>
            Você tem certeza que deseja se inscrever na turma{' '}
            <strong>
              {confirmEnrollTurma?.nm_descricao ||
                `Turma ${confirmEnrollTurma?.id_turma}`}
            </strong>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmEnrollTurma(null)}
            color='inherit'
            disabled={enrollLoading}
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmEnroll}
            variant='contained'
            color='primary'
            disabled={enrollLoading}
            startIcon={
              enrollLoading ? (
                <CircularProgress size={18} color='inherit' />
              ) : null
            }
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          >
            {enrollLoading ? 'Inscrevendo...' : 'Confirmar inscrição'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CursosDisponiveisPage;
